import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { isDesktopBrowser } from './constants/deviceDetect';
import ReactDOM from 'react-dom';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { pressingFetch, authenticatedFetch, getPresserSession, clearPressingSession } from './AuthComponent';
import { useAuth } from './AuthComponent';
import { useStore } from './StoreContext';
import SmartProductImage from './SmartProductImage';
import NumberPreviewThumb, { buildFontFaceCss } from './NumberPreviewThumb';
import { compareBins, isWarehouseLocation } from './binSort';
import CameraScanner from './CameraScanner';
import { playSuccess, playError, playOrderFinished, preloadSounds } from './sounds';
import PortalParticipantsFooter from './PortalParticipantsFooter';
import { useIdleLock } from './hooks';
import { synthesizeJumbledBundleNotifications } from './jumbledBundleUtils';
import { useT } from './i18n/I18nContext';
import NotificationBroadcastModal from './NotificationBroadcastModal';
import ManagerOverrideModal from './ManagerOverrideModal';
import ManagerOverrideBanner from './ManagerOverrideBanner';
import { MANAGER_BARCODE_RX } from './PressingOrderBoard';
import PortalDailySummary from './PortalDailySummary';

// 15 minutes of no user activity in the portal → wipe presser session and
// bounce back to PIN login. Work-in-progress is already persisted per-action
// in the DB, so nothing is lost.
const IDLE_LOCK_MS = 15 * 60 * 1000;

// ── LocationPickerModal ─────────────────────────────────────────────────────
// Shown when an order spans multiple locations the user has access to.
// Not dismissable by clicking outside or pressing Escape — user must pick
// a location or go back.
export function LocationPickerModal({ candidates, onPick, onCancel }) {
    const t = useT();
    // Block Escape key
    useEffect(() => {
        const trap = (e) => { if (e.key === 'Escape') e.preventDefault(); };
        document.addEventListener('keydown', trap, true);
        return () => document.removeEventListener('keydown', trap, true);
    }, []);

    return ReactDOM.createPortal(
        <div className="loc-picker-backdrop">
            <div className="loc-picker-modal">
                <div className="loc-picker-icon">📍</div>
                <div className="loc-picker-title">{t('Which location are you working at?')}</div>
                <div className="loc-picker-sub">
                    {t('This order is split across multiple locations. Choose the one you\'re working at — your scans and saves will be recorded there.')}
                </div>
                {candidates.map(loc => (
                    <button
                        key={loc.shopify_location_id}
                        className="loc-picker-btn"
                        onClick={() => onPick(loc)}
                    >
                        {loc.location_name || loc.shopify_location_id}
                    </button>
                ))}
                <button className="loc-picker-cancel" onClick={onCancel}>
                    {t('← Cancel — Back to Search')}
                </button>
            </div>
        </div>,
        document.body
    );
}

// ── Order Search ────────────────────────────────────────────────────────────

function OrderSearch({ shopifyLocationId, fetchFn, presserSession, assignments, locationMap, onSelect, bootstrapError = '' }) {
    const t = useT();
    const [q, setQ]               = useState('');
    const [results, setResults]   = useState([]);
    const [loading, setLoading]   = useState(false);
    const [searched, setSearched] = useState(false);
    const [openingId, setOpeningId] = useState(null);
    const [cameraOpen, setCameraOpen] = useState(false);
    // Location picker state: pending order awaiting location choice
    const [pendingOrder, setPendingOrder] = useState(null); // order row
    const [locCandidates, setLocCandidates] = useState([]); // assignments for this order's store
    const inputRef                = useRef(null);
    const debounceRef             = useRef(null);
    const autoSelectRef           = useRef(false); // true after a camera scan

    useEffect(() => {
        preloadSounds();
        return () => clearTimeout(debounceRef.current);
    }, []);

    // Keep search input focused for Bluetooth scanner support.
    useEffect(() => {
        const el = inputRef.current;
        if (!el) return;
        const handleBlur = () => { setTimeout(() => el.focus(), 50); };
        el.addEventListener('blur', handleBlur);
        return () => el.removeEventListener('blur', handleBlur);
    }, []);

    const search = useCallback((query) => {
        if (query.trim().length < 2) { setResults([]); setSearched(false); return Promise.resolve([]); }
        setLoading(true);

        // Admin sidebar mode (no presser session): single-location legacy path
        if (!presserSession || !assignments || assignments.length === 0) {
            return fetchFn(`/api/picking/orders?shopify_location_id=${encodeURIComponent(shopifyLocationId)}&q=${encodeURIComponent(query)}`)
                .then(r => r.json())
                .then(data => { setResults(data); setSearched(true); return data; })
                .catch(() => { setResults([]); return []; })
                .finally(() => setLoading(false));
        }

        // Multi-location presser: group assignments by store_key and fan out
        const byStore = {};
        for (const a of assignments) {
            if (!byStore[a.store_key]) byStore[a.store_key] = [];
            byStore[a.store_key].push(a.shopify_location_id);
        }
        const storeKeys = Object.keys(byStore);
        const requests = storeKeys.map(sk => {
            const csv = byStore[sk].join(',');
            return fetchFn(
                `/api/picking/orders?shopify_location_ids=${encodeURIComponent(csv)}&q=${encodeURIComponent(query)}`,
                undefined,
                sk,
            )
                .then(r => r.ok ? r.json() : [])
                .then(list => (Array.isArray(list) ? list : []).map(o => {
                    const locInfo = locationMap.get(String(o.shopify_location_id)) || {};
                    return {
                        ...o,
                        __store_key:     sk,
                        __location_name: locInfo.location_name || '',
                    };
                }))
                .catch(() => []);
        });
        return Promise.all(requests)
            .then(lists => {
                const merged = lists.flat();
                setResults(merged);
                setSearched(true);
                return merged;
            })
            .catch(() => { setResults([]); return []; })
            .finally(() => setLoading(false));
    }, [shopifyLocationId, fetchFn, presserSession, assignments, locationMap]);

    const handleChange = (e) => {
        const v = e.target.value;
        setQ(v);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => search(v), 400);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            clearTimeout(debounceRef.current);
            autoSelectRef.current = true; // auto-select if exactly 1 result (covers Bluetooth scanner)
            search(q);
        }
    };

    // Open order at the chosen location. (Lock removed — backend now uses
    // session-close events written from PickingView on save/complete.)
    const openOrder = async (order, orderLocId, orderStoreKey) => {
        setOpeningId(order.order_id);
        try {
            onSelect(order.order_id, { orderShopifyLocationId: orderLocId, orderStoreKey });
        } finally {
            setOpeningId(null);
        }
    };

    const handleSelect = async (order) => {
        if (openingId) return;
        // Admin sidebar view has no presser session — skip locking and navigate directly
        if (!presserSession) {
            onSelect(order.order_id);
            return;
        }
        // Use the clicked order's own store/location (fan-out tagged), not the session's
        const orderStoreKey = order.__store_key || (assignments?.[0]?.store_key) || null;
        const orderLocId    = order.shopify_location_id || (assignments?.[0]?.shopify_location_id) || '';

        // Multi-location check: only show the picker when the order is ACTUALLY at
        // multiple of the user's assigned locations. Without this filter the picker
        // fired any time the user had >1 assignments — even for single-location
        // orders — forcing a meaningless choice.
        if (assignments && assignments.length > 1 && orderStoreKey) {
            const storeCandidates = assignments.filter(a => a.store_key === orderStoreKey);
            if (storeCandidates.length > 1) {
                let cacheLocs = [];
                try {
                    const r = await fetchFn(`/api/orders/${order.order_id}/cache-locations`, undefined, orderStoreKey);
                    if (r.ok) {
                        const j = await r.json();
                        cacheLocs = (j.locations || []).map(l => String(l.shopify_location_id));
                    }
                } catch {}
                const orderHere = storeCandidates.filter(a => cacheLocs.includes(String(a.shopify_location_id)));
                if (orderHere.length > 1) {
                    setPendingOrder({ ...order, __resolved_store_key: orderStoreKey });
                    setLocCandidates(orderHere);
                    return; // wait for user to pick location
                }
                if (orderHere.length === 1) {
                    await openOrder(order, orderHere[0].shopify_location_id, orderStoreKey);
                    return;
                }
                // 0 hits or fetch error → fall through to the search-result's sloc.
            }
        }

        // Single-location path (or single-assignment): proceed immediately
        await openOrder(order, orderLocId, orderStoreKey);
    };

    // Auto-select whenever a search narrows to exactly one order (typed, Enter, or scan).
    // Sounds only play for scan / Enter (autoSelectRef), matching Packing Portal's silent-type behavior.
    useEffect(() => {
        if (results.length === 1) {
            if (autoSelectRef.current) { autoSelectRef.current = false; playSuccess(); }
            handleSelect(results[0]);
        } else if (autoSelectRef.current) {
            autoSelectRef.current = false;
            playError();
        }
    }, [results]); // eslint-disable-line react-hooks/exhaustive-deps

    const showBadge = (presserSession && assignments && assignments.length > 1);

    return (
        <div className="os-wrapper">
            {/* Location picker modal — shown when order spans multiple locations user can access */}
            {pendingOrder && locCandidates.length > 1 && (
                <LocationPickerModal
                    candidates={locCandidates}
                    onPick={(loc) => {
                        const order = pendingOrder;
                        setPendingOrder(null);
                        setLocCandidates([]);
                        openOrder(order, loc.shopify_location_id, order.__resolved_store_key);
                    }}
                    onCancel={() => { setPendingOrder(null); setLocCandidates([]); }}
                />
            )}
            {cameraOpen && (
                <CameraScanner
                    onDetect={(barcode) => {
                        autoSelectRef.current = true;
                        setCameraOpen(false);
                        setQ(barcode);
                        search(barcode);
                    }}
                    onClose={() => setCameraOpen(false)}
                />
            )}
            {bootstrapError && <div className="pkp-bootstrap-error">{bootstrapError}</div>}
            <div className="os-input-wrap">
                <input
                    ref={inputRef}
                    value={q}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    placeholder={t('Search by customer name or order number…')}
                    autoFocus
                    className="os-input"
                    onFocus={e => e.target.style.borderColor = '#1a4fa0'}
                    onBlur={e => e.target.style.borderColor = '#c9cccf'}
                />
                <button
                    className="pkp-camera-btn"
                    onClick={() => setCameraOpen(true)}
                    title={t('Scan order barcode')}
                >
                    📷
                </button>
                <span className="os-search-icon">🔍</span>
            </div>
            {loading && <p className="os-status">{t('Searching…')}</p>}
            {!loading && searched && results.length === 0 && (
                <p className="os-status">{t('No unfulfilled orders found for "{q}"', { q })}</p>
            )}
            {results.map(order => (
                <div
                    key={order.order_id}
                    onClick={() => handleSelect(order)}
                    className="os-result"
                    style={{ opacity: openingId === order.order_id ? 0.55 : 1, cursor: 'pointer' }}
                >
                    <div>
                        <div className="os-result__name">
                            {order.customer_name || '—'}
                            {showBadge && order.__location_name && (
                                <span className="pkp-order-badge">{order.__location_name}</span>
                            )}
                        </div>
                        <div className="os-result__meta">
                            {order.order_name} · {t(order.items_count === 1 ? '{n} item' : '{n} items', { n: order.items_count })}
                        </div>
                    </div>
                    {openingId !== order.order_id && <div className="os-result__arrow">›</div>}
                    {openingId === order.order_id && <div className="os-result__arrow">…</div>}
                </div>
            ))}
        </div>
    );
}

// ── buildDisplayGroups ───────────────────────────────────────────────────────
// Aggregates SKU-less embellishment line items that share the same normalized
// title into a single display card. Items with real SKUs (non-`__no_sku__`
// group keys) pass through one-to-one.
//
// Each output group carries `_underlyingIndices` (array of original sku_groups
// indices) so that the save-state POST — which POSTs per-underlying-index data
// — is completely unaffected.
function buildDisplayGroups(rawGroups) {
    if (!Array.isArray(rawGroups)) return [];
    const out = [];
    const titleBuckets = new Map();
    rawGroups.forEach((g, i) => {
        const gk = g.group_key || '';
        const isNoSku = gk.startsWith('__no_sku__');
        const title = (g.title || '').trim();
        const canAggregate = isNoSku && title;
        if (!canAggregate) {
            out.push({
                ...g,
                _underlyingIndices: [i],
                _isAggregated: false,
            });
            return;
        }
        const key = title.toLowerCase();
        const existing = titleBuckets.get(key);
        if (existing) {
            existing._underlyingIndices.push(i);
            // Sum quantities + union players (dedup on name#number|label).
            existing.quantity += (g.quantity || 0);
            const seen = new Set(existing.players.map(p =>
                `${p.name || ''}#${p.number || ''}|${p.label || ''}`));
            for (const p of (g.players || [])) {
                const k = `${p.name || ''}#${p.number || ''}|${p.label || ''}`;
                if (!seen.has(k)) {
                    existing.players.push(p);
                    seen.add(k);
                }
            }
            existing.require_scanning = existing.require_scanning || !!g.require_scanning;
            // Keep first underlying group_key as the display group_key so any
            // group_key-dependent code (e.g. picked-badge UI) maps to a stable
            // identifier. Backend save still POSTs per-underlying-index data.
        } else {
            const seed = {
                ...g,
                _underlyingIndices: [i],
                _isAggregated: false,  // becomes true if a second underlying joins
                players: Array.isArray(g.players) ? [...g.players] : [],
            };
            titleBuckets.set(key, seed);
            out.push(seed);
        }
    });
    // Mark groups with > 1 underlying as aggregated.
    for (const grp of out) {
        if ((grp._underlyingIndices || []).length > 1) grp._isAggregated = true;
    }
    return out;
}

// ── PickingView ──────────────────────────────────────────────────────────────

function PickingView({ orderId, orderShopifyLocationId, orderStoreKey, orderLocationName, presserSession, fetchFn, onBack }) {
    const t = useT();
    // Use the order's own store/location for every request, not the session's.
    const shopifyLocationId = orderShopifyLocationId;
    // Warehouse-location override: scan_only_picker users NEVER see manual +/-
    // buttons at any location whose name matches /Warehouse/i, regardless of
    // the per-product custom.require_scanning opt-out.
    const isAtWarehouse = /warehouse/i.test(orderLocationName || '');
    const { user: adminUser } = useAuth();
    const adminName = adminUser
        ? (adminUser.first_name && adminUser.last_name
            ? `${adminUser.first_name} ${adminUser.last_name}`
            : adminUser.user)
        : null;
    const [order, setOrder]     = useState(null);
    const [picked, setPicked]   = useState({}); // index → count
    const [scannedCounts, setScannedCounts] = useState({}); // index → qty picked by scan
    // Last-scan / last-manual ISO timestamp per item index — sent in the
    // batched save so each event row gets its real moment in time instead of
    // sharing NOW() with every other event in the save.
    const [scannedAt, setScannedAt] = useState({});
    const [markedAt,  setMarkedAt]  = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError]     = useState('');
    const [flash, setFlash]     = useState(null); // 'green' | 'red' | null
    const [scanInput, setScanInput] = useState('');
    const [modal, setModal]     = useState('');
    // Debag confirmation gate (2026-09-03): index → true once that bundle
    // base row has been debagged. Rows already fully picked when the order
    // loads (or remembered from sessionStorage for this order + shift) are
    // pre-seeded as debagged — see the load effect below. debagLoadedRef
    // guards the persist effect from writing an empty array to
    // sessionStorage before the initial seed has run.
    const [debagged, setDebagged] = useState({}); // index → true
    const [debagBanner, setDebagBanner] = useState('');
    const debagLoadedRef = useRef(false);
    // Manager Override — order-wide, time-limited (60s from verify).
    // `managerOverride = {manager_user_id, first_name, last_name, manager_name, expiresAt}`.
    // While active, any manual tap on any SKU in this order is allowed and
    // the whole background turns pastel blue as a visual cue. Auto-clears
    // when expiresAt passes.
    const MANAGER_OVERRIDE_MS = 60 * 1000;
    const [pendingMgrBarcode, setPendingMgrBarcode] = useState(null);
    const [managerOverride, setManagerOverride]     = useState(null);
    const [_mgrNow, _setMgrNow]                     = useState(() => Date.now());
    useEffect(() => {
        if (!managerOverride) return;
        const id = setInterval(() => _setMgrNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, [managerOverride]);
    useEffect(() => {
        if (managerOverride && _mgrNow > managerOverride.expiresAt) {
            setManagerOverride(null);
        }
    }, [_mgrNow, managerOverride]);
    const managerActive = !!(managerOverride && _mgrNow < managerOverride.expiresAt);
    const [completing, setCompleting] = useState(false);
    const [completeError, setCompleteError] = useState('');
    const [showCompleteModal, setShowCompleteModal] = useState(false);
    const [cameraOpen, setCameraOpen] = useState(false);
    const [sortBy, setSortBy]         = useState('location'); // 'location' | 'type'
    // Session-scoped sticky filter: remembers choice while this presser session lasts.
    const [typeFilter, setTypeFilter] = useState(() => {
        const v = sessionStorage.getItem('pkp_type_filter');
        return (v === 'base' || v === 'embellishments') ? v : 'all';
    });
    useEffect(() => { sessionStorage.setItem('pkp_type_filter', typeFilter); }, [typeFilter]);
    const [numberPreviews, setNumberPreviews] = useState({});
    const [pendingNotifications, setPendingNotifications] = useState([]);
    // SKU Tools: {sku: message} for every SKU on this order that has an
    // active notification. Rendered as a red badge under the SKU.
    const [skuNotifications, setSkuNotifications] = useState({});
    useEffect(() => {
        if (!orderId) return;
        fetchFn(`/api/sku-notifications/for-order/${orderId}`)
            .then(r => r.ok ? r.json() : { sku_notifications: {} })
            .then(j => setSkuNotifications(j.sku_notifications || {}))
            .catch(() => setSkuNotifications({}));
    }, [orderId, fetchFn]);

    const initialPickedRef = useRef({});
    // group_key → units of a split line picked at the order's other locations.
    const pickedElsewhereRef = useRef({});
    // 2026-08-24: serialize all pick-state saves. Each save advances
    // initialPickedRef (the "already-saved" baseline) only AFTER its POST
    // resolves. When saves overlap (rapid scans on slow warehouse wifi), a
    // later save was computing its delta against a not-yet-advanced baseline,
    // so qty_after came out CUMULATIVE (1,2,3,4,5) instead of per-save (1,1,1,
    // 1,1) — which broke picker-report unit counts. savingRef makes saves
    // non-overlapping: the debounced auto-save skips while one is in flight
    // (the debounce re-fires on the next pick change), and the terminal
    // Back/Complete saves await the slot so their delta is computed against
    // the freshly-advanced baseline. Never lets a pick get lost — a skipped
    // auto-save is re-covered by the next debounce or the exit save.
    const savingRef = useRef(false);
    const waitForSaveSlot = useCallback(async () => {
        for (let i = 0; i < 240 && savingRef.current; i++) {
            await new Promise((r) => setTimeout(r, 25));  // ~6s max wait
        }
    }, []);
    const scanRef         = useRef(null);
    const scanBufRef      = useRef('');
    const scanFocusDivRef = useRef(null);
    const readyRef        = useRef(false); // blocks residual chars from order-selection barcode
    // 2026-05-19: session_start is now written server-side via POST /session-open
    // at order-panel mount. session_end fires via /session-close. Backend
    // tracks "open session for (presser, order)" state — no client-side ref
    // needed. Orphan sweep handles missed closes.
    const sessionOpenedRef = useRef(false);  // local guard to call /session-open exactly once

    // Clear any manager override the moment the operator switches to a
    // different order. Prevents override leakage across orders — if Aaron
    // used a manager PIN to pick a scan-only SKU on order A, opening order
    // B (or reopening A) must NOT still be armed.
    useEffect(() => {
        setManagerOverride(null);
        setPendingMgrBarcode(null);
    }, [orderId]);

    // Load order detail
    useEffect(() => {
        setLoading(true);
        debagLoadedRef.current = false;
        setDebagged({});
        setDebagBanner('');
        fetchFn(`/api/picking/orders/${orderId}?shopify_location_id=${encodeURIComponent(shopifyLocationId)}`, undefined, orderStoreKey)
            .then(r => r.json())
            .then(data => {
                setOrder(data);
                const _pending = [
                    ...synthesizeJumbledBundleNotifications(
                        data?.jumbled_bundles, data?.order_name),
                    ...(Array.isArray(data?.active_notifications) ? data.active_notifications : []),
                ];
                if (_pending.length) setPendingNotifications(_pending);
                // Load saved state, then merge with groups
                return fetchFn(`/api/picking/orders/${orderId}/state?shopify_location_id=${encodeURIComponent(shopifyLocationId || '')}`, undefined, orderStoreKey)
                    .then(r => r.ok ? r.json() : [])
                    .then(savedState => {
                        const savedMap = {};
                        // A line split across locations shares one saved count; show only
                        // this location's units and remember the rest for the save
                        // (2026-10-30, B2B-96037).
                        const elsewhere = {};
                        savedState.forEach(s => {
                            if (s.split_line) {
                                savedMap[s.group_key] = s.qty_picked_here || 0;
                                elsewhere[s.group_key] = s.qty_picked_elsewhere || 0;
                            } else {
                                savedMap[s.group_key] = s.qty_picked;
                            }
                        });
                        pickedElsewhereRef.current = elsewhere;
                        const init = {};
                        (data.sku_groups || []).forEach((g, i) => {
                            const key = g.group_key || g.sku || '';
                            init[i] = savedMap[key] || 0;
                        });
                        setPicked(init);
                        initialPickedRef.current = { ...init };

                        // Debag confirmation gate: rows already fully picked when the
                        // order is opened count as debagged, unioned with whatever this
                        // shift already debagged (sessionStorage, keyed by group_key so
                        // it survives leaving and returning to the same order).
                        let _rememberedKeys = [];
                        try {
                            _rememberedKeys = JSON.parse(sessionStorage.getItem(`pkp_debag_${orderId}`) || '[]');
                        } catch { _rememberedKeys = []; }
                        const dbg = {};
                        (data.sku_groups || []).forEach((g, i) => {
                            if (!g.debag) return;
                            const key = g.group_key || g.sku || '';
                            if ((init[i] || 0) >= g.quantity || _rememberedKeys.includes(key)) {
                                dbg[i] = true;
                            }
                        });
                        setDebagged(dbg);
                        debagLoadedRef.current = true;
                    });
            })
            .catch(() => setError(t('Failed to load order.')))
            .finally(() => setLoading(false));

        fetchFn(`/api/orders/${orderId}/number-previews`, undefined, orderStoreKey)
            .then(r => r.ok ? r.json() : { previews: {} })
            .then(d => setNumberPreviews(d.previews || {}))
            .catch(() => setNumberPreviews({}));
    }, [orderId, shopifyLocationId, orderStoreKey, fetchFn]);

    // Persist debag clicks to sessionStorage per order — nothing is written to
    // the server or the timeline. Skipped until the initial seed (above) has
    // run, so we never clobber a remembered row with an empty array while the
    // order is still loading.
    useEffect(() => {
        if (!debagLoadedRef.current || !order) return;
        const groups = order.sku_groups || [];
        const keys = Object.keys(debagged)
            .filter(i => debagged[i])
            .map(i => {
                const g = groups[i];
                return g ? (g.group_key || g.sku || '') : '';
            })
            .filter(Boolean);
        try {
            sessionStorage.setItem(`pkp_debag_${orderId}`, JSON.stringify(keys));
        } catch { /* sessionStorage unavailable — non-fatal, just re-asks next load */ }
    }, [debagged, order, orderId]);

    // Focus the visible scan input so keyboard-wedge scanners (iPad
    // Bluetooth, Android USB/BT, Zebra, Honeywell) all emit their
    // keystrokes into the same target. Runs on mount AND whenever `order`
    // becomes available — the visible input only renders once the order
    // is loaded, so the initial mount would otherwise fall through to
    // the hidden div and leave the picker's next scan invisible. Delay
    // readyRef to drop residual barcode chars from the order-selection
    // scan.
    useEffect(() => {
        (scanRef.current || scanFocusDivRef.current)?.focus();
        const t = setTimeout(() => { readyRef.current = true; }, 600);
        return () => clearTimeout(t);
    }, []);
    useEffect(() => {
        // Refocus the visible input the moment the <input ref=scanRef>
        // renders. Depends on `loading` transitioning false AND the input
        // actually being mounted (scanRef.current non-null). Without the
        // loading dep, the initial run fires while the "Loading order…"
        // div is showing (input not yet rendered → scanRef.current is
        // null → focus falls through to the hidden div and user sees no
        // caret). requestAnimationFrame gives React a frame to commit
        // the input before we grab focus.
        if (order && !loading && scanRef.current) {
            requestAnimationFrame(() => scanRef.current?.focus());
        }
    }, [order, loading]);

    const refocusScan = useCallback(() => {
        // Prefer the visible <input> so the user can immediately type/scan
        // after dismissing a modal (e.g. "Product Not In Order"). Fall back
        // to the hidden div — that div exists only for iPad Bluetooth
        // scanners that emit keystrokes when no input has focus.
        setTimeout(() => {
            (scanRef.current || scanFocusDivRef.current)?.focus();
        }, 50);
    }, []);

    // Fire /session-open the moment the order panel first has data. Backend is
    // idempotent — duplicate opens for the same (presser, order) no-op. We
    // also guard locally to make exactly one POST per mount.
    useEffect(() => {
        if (!order || sessionOpenedRef.current) return;
        sessionOpenedRef.current = true;
        try {
            fetchFn(`/api/picking/orders/${orderId}/session-open`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    portal_userid:       presserSession?.presser_id || null,
                    portal_username:     presserSession
                        ? `${presserSession.first_name} ${presserSession.last_name}`
                        : adminName,
                    shopify_location_id: shopifyLocationId,
                }),
            }, orderStoreKey).catch(() => {});
        } catch {}
    }, [order, orderId, orderStoreKey, shopifyLocationId, fetchFn, presserSession, adminName]);

    // Auto-save the current pick state without closing the session. Fired by
    // a debounced effect whenever `picked` changes so a pull-to-refresh /
    // accidental navigation / lock-screen can never lose in-progress picks —
    // the server always has the latest state and the mount-time restore
    // (line ~490) will rehydrate it. Mirrors handleBackAndSave's POST payload
    // exactly (same event_type='pick_save', same shadow-write fields, same
    // manager-override attribution) but skips the session-close +
    // navigation. Bug 2026-08-18: user reports losing picking history when
    // they pull-to-refresh on iPad Safari mid-pick.
    const autoSavePicks = useCallback(async () => {
        if (!order?.sku_groups) return;
        const snapshot = { ...picked };
        // Skip when nothing has changed vs. the last saved baseline —
        // avoids a POST storm during the initial mount before any pick
        // actually happens.
        const anyDelta = order.sku_groups.some((_, i) =>
            (snapshot[i] || 0) !== (initialPickedRef.current[i] || 0)
        );
        if (!anyDelta) return;
        // Serialize: if a save is already in flight, skip — the debounce
        // re-fires on the next pick change (and the exit save is a backstop),
        // so nothing is lost. This is what keeps qty_after per-save instead of
        // cumulative under rapid scanning.
        if (savingRef.current) return;
        const items = order.sku_groups.map((g, i) => {
            const delta = Math.max(0, (snapshot[i] || 0) - (initialPickedRef.current[i] || 0));
            const _liIds = Array.isArray(g?.line_item_ids) ? g.line_item_ids : [];
            const _firstLi = _liIds.length > 0 ? String(_liIds[0]) : null;
            const _foId = g?.fulfillment_order_id ? String(g.fulfillment_order_id) : null;
            return {
                group_key:   g.group_key || g.sku || `__no_sku__${i}`,
                title:       g.title,
                qty_total:   g.quantity,
                qty_picked:  snapshot[i] || 0,
                initial_qty_picked: initialPickedRef.current[i] || 0,
                qty_scanned: Math.min(scannedCounts[i] || 0, delta),
                qty_manual:  Math.max(0, delta - (scannedCounts[i] || 0)),
                scanned_at:  scannedAt[i] || null,
                marked_at:   markedAt[i] || null,
                line_item_id:         _firstLi,
                fulfillment_order_id: _foId,
                qty_picked_elsewhere: pickedElsewhereRef.current[g.group_key || g.sku || ''] ?? null,
            };
        });
        savingRef.current = true;
        try {
            const res = await fetchFn(`/api/picking/orders/${orderId}/state`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    order_name: order?.order_name,
                    shopify_location_id: shopifyLocationId,
                    portal_userid: presserSession?.presser_id || null,
                    portal_username: presserSession
                        ? `${presserSession.first_name} ${presserSession.last_name}`
                        : adminName,
                    items,
                    event_type: 'pick_save',
                    manager_override: managerActive ? {
                        user_id:    managerOverride.manager_user_id,
                        first_name: managerOverride.first_name,
                        last_name:  managerOverride.last_name,
                        name:       managerOverride.manager_name,
                    } : null,
                }),
            }, orderStoreKey);
            // Advance the baseline on success — mirrors handleBackAndSave.
            // Prevents re-emitting the same picks as dup events on the next
            // save. See the SURF-62440 comment on handleBackAndSave.
            if (res?.ok) {
                initialPickedRef.current = { ...snapshot };
            }
        } catch { /* silent — next save will retry */ }
        finally { savingRef.current = false; }
    }, [order, orderId, orderStoreKey, shopifyLocationId, picked,
        scannedCounts, scannedAt, markedAt, presserSession, adminName,
        managerActive, managerOverride, fetchFn]);

    // Debounced auto-save: 800ms after the last pick change, fire the save.
    // Guards against POST storms during rapid scanning while still landing
    // the save well before a typical pull-to-refresh gesture.
    useEffect(() => {
        const t = setTimeout(() => { autoSavePicks(); }, 800);
        return () => clearTimeout(t);
    }, [picked, autoSavePicks]);

    // Save current state and close session on back.
    const handleBackAndSave = async () => {
        // Wait for any in-flight auto-save to finish (it advances the baseline),
        // then claim the lock so our delta is computed against the up-to-date
        // baseline and can't overlap another save.
        await waitForSaveSlot();
        savingRef.current = true;
        // Snapshot pick state at the moment of save so we can update
        // initialPickedRef after a successful round-trip — see comment below.
        const snapshot = { ...picked };
        const items = (order?.sku_groups || []).map((g, i) => {
            const delta = Math.max(0, (snapshot[i] || 0) - (initialPickedRef.current[i] || 0));
            // 2026-08-02 Stage 1 shadow-write: pass the FIRST line_item_id
            // from the group + its owning FO through to the backend. Multi-LI
            // groups (same SKU used across multiple line items) get only the
            // first LI attributed for now; Stage 2 will handle unit-by-unit
            // attribution across the LI list. NULL for legacy/incomplete
            // groups — reader falls back to SKU aggregation.
            const _liIds = Array.isArray(g?.line_item_ids) ? g.line_item_ids : [];
            const _firstLi = _liIds.length > 0 ? String(_liIds[0]) : null;
            const _foId = g?.fulfillment_order_id ? String(g.fulfillment_order_id) : null;
            return {
                group_key:   g.group_key || g.sku || `__no_sku__${i}`,
                title:       g.title,
                qty_total:   g.quantity,
                qty_picked:  snapshot[i] || 0,
                // The qty_picked we saw on load — lets the backend tell the
                // difference between "I'm decrementing a row I picked earlier"
                // (write the lower value) and "I never touched this row"
                // (GREATEST-protect against concurrent picker's progress).
                initial_qty_picked: initialPickedRef.current[i] || 0,
                qty_scanned: Math.min(scannedCounts[i] || 0, delta),
                qty_manual:  Math.max(0, delta - (scannedCounts[i] || 0)),
                scanned_at:  scannedAt[i] || null,
                marked_at:   markedAt[i] || null,
                line_item_id:         _firstLi,
                fulfillment_order_id: _foId,
                qty_picked_elsewhere: pickedElsewhereRef.current[g.group_key || g.sku || ''] ?? null,
            };
        });
        try {
            const res = await fetchFn(`/api/picking/orders/${orderId}/state`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    order_name: order?.order_name,
                    shopify_location_id: shopifyLocationId,
                    portal_userid: presserSession?.presser_id || null,
                    portal_username: presserSession ? `${presserSession.first_name} ${presserSession.last_name}` : adminName,
                    items,
                    event_type: 'pick_save',
                    manager_override: managerActive ? {
                        user_id:    managerOverride.manager_user_id,
                        first_name: managerOverride.first_name,
                        last_name:  managerOverride.last_name,
                        name:       managerOverride.manager_name,
                    } : null,
                }),
            }, orderStoreKey);
            // Advance the "already-saved" baseline so a subsequent save with
            // no new picks (e.g. picker re-opens and clicks Back & Save
            // again) sees delta=0 and the backend skips event inserts. Bug
            // SURF-62440: without this, every save re-emitted all picks as
            // dup events with the same scanned_at/marked_at, producing
            // hundreds of duplicate pick events per long picking session.
            if (res?.ok) {
                initialPickedRef.current = { ...snapshot };
            }
        } catch { /* proceed even if save fails */ }
        finally { savingRef.current = false; }
        // Close session: backend writes _session_end immediately; no-op if no
        // open start exists for this (presser, order).
        try {
            await fetchFn(`/api/picking/orders/${orderId}/session-close`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    portal_userid:       presserSession?.presser_id || null,
                    portal_username:     presserSession
                        ? `${presserSession.first_name} ${presserSession.last_name}`
                        : adminName,
                    shopify_location_id: shopifyLocationId,
                }),
            }, orderStoreKey);
        } catch { /* don't block back navigation on a session-close failure */ }
        onBack();
    };

    // Flash screen
    const triggerFlash = (color) => {
        setFlash(color);
        setTimeout(() => setFlash(null), 600);
    };

    const showModal = useCallback((text) => {
        setModal(text);
        scanRef.current?.blur(); // hide keyboard when toast appears
    }, []);

    const dismissModal = useCallback(() => {
        setModal('');
        refocusScan();
    }, [refocusScan]);

    // Debag confirmation gate (2026-09-03): the first bundle-base row that is
    // fully picked (scan or manual) but not yet debagged. While this is >= 0,
    // every scan/tap for a DIFFERENT row is blocked — see processBarcode below
    // and the inline +/- handlers in the row render (the only two places pick
    // counts actually change; there is no separate manual-check function).
    // Scanning more units of a not-yet-complete SKU is never blocked; each
    // row's bar only appears once THAT row is full.
    const pendingDebagIdx = useMemo(() => {
        const groups = order?.sku_groups || [];
        for (let i = 0; i < groups.length; i++) {
            const g = groups[i];
            if (g.debag && (picked[i] || 0) >= g.quantity && !debagged[i]) return i;
        }
        return -1;
    }, [order, picked, debagged]);

    // Clears a row's debagged flag once it's decremented below its full
    // quantity — picking it back up to full will re-trigger the bar.
    const clearDebagIfNeeded = useCallback((idx, newVal) => {
        const g = (order?.sku_groups || [])[idx];
        if (!g || !g.debag) return;
        if (newVal < g.quantity) {
            setDebagged(prev => {
                if (!prev[idx]) return prev;
                const next = { ...prev };
                delete next[idx];
                return next;
            });
        }
    }, [order]);

    const debag = useCallback((idx) => {
        setDebagged(prev => ({ ...prev, [idx]: true }));
        playSuccess();
        setDebagBanner('');
    }, []);

    // Scan processing. Splits (same barcode, different #/name) resolve
    // top-down: the first group with matching barcode whose picked count is
    // still below its quantity gets the scan. When every matching group is
    // already fully picked, we show 'Already Fully Scanned'.
    const processBarcode = useCallback((value) => {
        if (!order || !value.trim()) return;
        const trimmed = value.trim();
        // Manager Override barcode intercept — see ManagerOverrideModal + the
        // corresponding logic in PressingOrderBoard. Opens the PIN keypad
        // and arms an override for the next SKU the picker taps manually.
        if (MANAGER_BARCODE_RX.test(trimmed.toUpperCase())) {
            setPendingMgrBarcode(trimmed.toUpperCase());
            return;
        }
        const groups = order.sku_groups || [];
        const matchIdxs = groups
            .map((g, i) => ({ g, i }))
            .filter(({ g }) => g.barcode && g.barcode === trimmed)
            .map(({ i }) => i);
        if (matchIdxs.length === 0) {
            triggerFlash('red');
            playError();
            showModal(t('Product Not In Order'));
            return;
        }
        const nextIdx = matchIdxs.find(i => (picked[i] || 0) < groups[i].quantity);
        if (nextIdx === undefined) {
            triggerFlash('red');
            playError();
            showModal(t('Already Fully Scanned'));
            return;
        }
        // Debag hard gate: a bar is pending on a DIFFERENT row. Buzz + banner,
        // ignore the scan entirely (count does not change).
        if (pendingDebagIdx >= 0 && pendingDebagIdx !== nextIdx) {
            playError();
            setDebagBanner(t('Debag {title} before continuing', { title: groups[pendingDebagIdx].title }));
            return;
        }
        triggerFlash('green');
        playSuccess();
        setPicked(prev => ({ ...prev, [nextIdx]: (prev[nextIdx] || 0) + 1 }));
        setScannedCounts(prev => ({ ...prev, [nextIdx]: (prev[nextIdx] || 0) + 1 }));
        setScannedAt(prev => ({ ...prev, [nextIdx]: new Date().toISOString() }));
    }, [order, picked, showModal, pendingDebagIdx]);

    // Capture Bluetooth scanner keystrokes at document level — no focused input,
    // so iOS never shows the keyboard. readyRef blocks residual chars from order scan.
    useEffect(() => {
        const handleKey = (e) => {
            if (!readyRef.current) return;
            const tag = document.activeElement?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA') return;
            if (e.key === 'Enter') {
                const val = scanBufRef.current.trim();
                scanBufRef.current = '';
                if (val) processBarcode(val);
            } else if (e.key.length === 1) {
                scanBufRef.current += e.key;
            }
        };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [processBarcode]);

    // Input key handler — for when the user taps the scan bar and types / scans directly
    const handleScanKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            processBarcode(scanInput);
            setScanInput('');
        }
    };

    // Aggregate SKU-less embellishments into display groups. Memoized against
    // the order object so it only recomputes when the order data changes.
    const displayGroups = useMemo(() => buildDisplayGroups(order?.sku_groups), [order]);

    // TP number overrides keyed by player name — RDS wins over the checkout-
    // embedded number for clubs on the override list. Rendered inline in the
    // per-item player line (yellow highlight on the changed number).
    const tpOverridesByName = useMemo(() =>
        ((order?.tp_number_overrides) || []).reduce((m, ov) => {
            if (ov && ov.player_name) m[ov.player_name] = ov;
            return m;
        }, {}),
    [order?.tp_number_overrides]);
    // Anonymous-name fallback: Number embellishment groups (Stahls Team US
    // etc.) surface players as {name: null, number: "38", label: null}, so
    // we can't match by name. Fall back to matching the row's number
    // against any override's `order_number` — but ONLY when that number is
    // unique across all overrides on this order. If two players had the
    // same pre-edit number (e.g. both #10) and only one has an override,
    // matching by number alone would mis-apply that override to the other
    // player's anon tile.
    const tpOverridesByOrderNumber = useMemo(() => {
        const seen = new Map();     // orderNumber → ov (or 'AMBIGUOUS')
        for (const ov of (order?.tp_number_overrides || [])) {
            if (!ov || !ov.order_number) continue;
            const k = String(ov.order_number);
            seen.set(k, seen.has(k) ? 'AMBIGUOUS' : ov);
        }
        const out = {};
        for (const [k, v] of seen.entries()) {
            if (v !== 'AMBIGUOUS') out[k] = v;
        }
        return out;
    }, [order?.tp_number_overrides]);

    // Completion check
    const groups = order?.sku_groups || [];
    const allPicked = displayGroups.length > 0 && displayGroups.every(g =>
        (g._underlyingIndices || []).reduce((s, idx) => s + (picked[idx] || 0), 0) >= g.quantity
    );

    // Auto-show completion modal the moment all items are picked AND no debag
    // bar is pending — if the very last unit picked happens to complete a
    // flagged bundle base row, hold the modal back so the picker sees the
    // amber bar instead of a disabled Complete button behind a full-screen
    // modal; it opens automatically once that bar is cleared. Sound plays
    // once per allPicked transition regardless, so the picker still gets
    // audible confirmation the moment counts are all in.
    useEffect(() => {
        if (allPicked && pendingDebagIdx < 0) {
            setShowCompleteModal(true);
        }
    }, [allPicked, pendingDebagIdx]);
    useEffect(() => {
        if (allPicked) playOrderFinished();
    }, [allPicked]); // eslint-disable-line react-hooks/exhaustive-deps

    // Embellishment / customization services — always sort last within any group
    const EMBELLISHMENT_TYPES_RX = /^(name|number|logo|embroidered logo|embroidered number|crest|decals)$/i;
    const isCustomization = (g) => EMBELLISHMENT_TYPES_RX.test((g.product_type || '').trim());
    const locationKey = (g) => {
        const pt = g.product_type || g.title || '';
        return isCustomization(g) ? `\uffff${pt}` : pt;
    };
    // 'Product' sort matches order detail: alphabetical by title, customizations last
    const productKey = (g) => isCustomization(g) ? `\uffff${g.title || ''}` : (g.title || '');

    // Build sorted display groups (picked state stays keyed by original index).
    // Apply the Base/Embellishments filter first, then sort the remainder.
    // Uses display groups (which may aggregate identical SKU-less embellishments)
    // rather than raw group indices so aggregated cards sort/filter as one unit.
    const sortedDisplayGroups = useMemo(() => {
        let items = displayGroups.filter(g => {
            if (typeFilter === 'all') return true;
            const isEmb = isCustomization(g);
            return typeFilter === 'embellishments' ? isEmb : !isEmb;
        });
        items = [...items]; // copy before in-place sort
        if (sortBy === 'location') {
            // Bijective base-26 aisle order at Warehouse locations: A,B,...,Z,
            // AA,AB,... — the standard apparel-aisle picking sequence. Falls
            // back to the legacy alphanumeric localeCompare at non-Warehouse
            // (Teams etc.) locations where bins aren't aisle-coded.
            const useBinSort = isWarehouseLocation(orderLocationName);
            items.sort((a, b) => {
                const la = a.location_value || '';
                const lb = b.location_value || '';
                // No-location items go last; within same location fall back to product title (customizations last)
                if (!la && !lb) return locationKey(a).localeCompare(locationKey(b));
                if (!la) return 1;
                if (!lb) return -1;
                const cmp = useBinSort
                    ? compareBins(la, lb)
                    : la.localeCompare(lb, undefined, { numeric: true, sensitivity: 'base' });
                if (cmp !== 0) return cmp;
                return locationKey(a).localeCompare(locationKey(b));
            });
        } else if (sortBy === 'type') {
            items.sort((a, b) => productKey(a).localeCompare(productKey(b)));
        }
        return items;
    }, [displayGroups, sortBy, typeFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    // Complete order
    const handleComplete = async () => {
        setCompleting(true);
        setCompleteError('');
        // Serialize with the auto-save: wait for any in-flight save (advances
        // the baseline), then hold the lock across the pre-complete state save
        // so its delta is computed against the up-to-date baseline.
        await waitForSaveSlot();
        savingRef.current = true;
        // Save full pick state before completing
        const items = (order?.sku_groups || []).map((g, i) => {
            const scanned = Math.min(scannedCounts[i] || 0, g.quantity);
            const delta   = Math.max(0, g.quantity - (initialPickedRef.current[i] || 0));
            // 2026-08-02 Stage 1 shadow-write attribution — same as handleBackAndSave.
            const _liIds = Array.isArray(g?.line_item_ids) ? g.line_item_ids : [];
            const _firstLi = _liIds.length > 0 ? String(_liIds[0]) : null;
            const _foId = g?.fulfillment_order_id ? String(g.fulfillment_order_id) : null;
            return {
                group_key:   g.group_key || g.sku || `__no_sku__${i}`,
                title:       g.title,
                qty_total:   g.quantity,
                qty_picked:  g.quantity, // all picked on complete
                initial_qty_picked: initialPickedRef.current[i] || 0,
                qty_scanned: Math.min(scanned, delta),
                qty_manual:  Math.max(0, delta - Math.min(scanned, delta)),
                scanned_at:  scannedAt[i] || null,
                marked_at:   markedAt[i] || null,
                line_item_id:         _firstLi,
                fulfillment_order_id: _foId,
                qty_picked_elsewhere: pickedElsewhereRef.current[g.group_key || g.sku || ''] ?? null,
            };
        });
        // Snapshot what we're about to save so we can advance the baseline.
        const completedSnapshot = {};
        (order?.sku_groups || []).forEach((g, i) => { completedSnapshot[i] = g.quantity; });
        const completeRes = await fetchFn(`/api/picking/orders/${orderId}/state`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                order_name: order?.order_name,
                shopify_location_id: shopifyLocationId,
                portal_userid: presserSession?.presser_id || null,
                portal_username: presserSession ? `${presserSession.first_name} ${presserSession.last_name}` : adminName,
                items,
                event_type: 'pick_complete',
            }),
        }, orderStoreKey).catch(() => null);
        // Advance the baseline (see handleBackAndSave for full rationale).
        if (completeRes?.ok) {
            initialPickedRef.current = { ...completedSnapshot };
        }
        savingRef.current = false;  // release before the /complete call
        try {
            const res = await fetchFn(`/api/picking/orders/${orderId}/complete`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    presser_id: presserSession?.presser_id || 0,
                    shopify_location_id: shopifyLocationId,
                }),
            }, orderStoreKey);
            if (res.ok) {
                // Close session: backend writes _session_end immediately.
                try {
                    await fetchFn(`/api/picking/orders/${orderId}/session-close`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            portal_userid:       presserSession?.presser_id || null,
                            portal_username:     presserSession
                                ? `${presserSession.first_name} ${presserSession.last_name}`
                                : adminName,
                            shopify_location_id: shopifyLocationId,
                        }),
                    }, orderStoreKey);
                } catch { /* don't block completion on a session-close failure */ }
                setShowCompleteModal(false);
                onBack();
            } else {
                setCompleteError(t('Could not update Shopify — tap Mark as Picked to retry.'));
            }
        } catch {
            setCompleteError(t('Could not update Shopify — tap Complete to retry.'));
        } finally {
            setCompleting(false);
        }
    };

    // ── Render guards ────────────────────────────────────────────────────────

    if (loading) return <div className="pkp-loading">{t('Loading order…')}</div>;

    if (error) return (
        <div className="pkp-error-state">
            <div className="pkp-error-msg">{error}</div>
            <button onClick={onBack} className="btn btn--secondary">{t('← Back to search')}</button>
        </div>
    );

    if (groups.length === 0) return (
        <div className="pkp-error-state">
            <div className="pkp-error-msg">{t('No items to pick on this order.')}</div>
            <button onClick={onBack} className="btn btn--secondary">{t('← Back to search')}</button>
        </div>
    );

    const pickedCount = displayGroups.filter(g =>
        (g._underlyingIndices || []).reduce((s, idx) => s + (picked[idx] || 0), 0) >= g.quantity
    ).length;

    return (
        <div className={`pkp-view${managerActive ? ' pkp-view--mgr-active' : ''}`}>
            {managerActive && (
                <ManagerOverrideBanner expiresAt={managerOverride.expiresAt} now={_mgrNow} />
            )}
            <NotificationBroadcastModal
                notifications={pendingNotifications}
                onAcknowledge={() => setPendingNotifications([])}
                compact
            />
            <ManagerOverrideModal
                open={!!pendingMgrBarcode}
                barcode={pendingMgrBarcode || ''}
                fetchFn={fetchFn}
                onClose={() => setPendingMgrBarcode(null)}
                onVerified={(mgr) => {
                    setManagerOverride({ ...mgr, expiresAt: Date.now() + MANAGER_OVERRIDE_MS });
                    setPendingMgrBarcode(null);
                }}
            />
            <style>{buildFontFaceCss(numberPreviews)}</style>
            {/* Hidden focus target for iPad Bluetooth scanner */}
            <div
                ref={scanFocusDivRef}
                tabIndex={0}
                aria-hidden="true"
                style={{ position: 'fixed', top: -9999, width: 1, height: 1, opacity: 0, outline: 'none' }}
            />

            {/* Full-screen flash overlay — portaled so iOS fixed positioning works */}
            {flash && ReactDOM.createPortal(
                <div className={`pkp-flash pkp-flash--${flash}`} />,
                document.body
            )}

            {/* Scan result toast — tap anywhere to dismiss */}
            {modal && ReactDOM.createPortal(
                <div className="pp-scan-toast pp-scan-toast--clickable" onClick={dismissModal}>
                    <div className="pp-scan-toast__text">
                        ⛔ {modal}
                        <span className="pp-scan-toast__dismiss">{t('Tap to dismiss')}</span>
                    </div>
                </div>,
                document.body
            )}

            {/* Working-at banner — shown when user explicitly picked a location */}
            {orderLocationName && (
                <div className="working-at-banner">
                    <span className="working-at-banner__icon">📍</span>
                    {t('Working at:')} {orderLocationName}
                </div>
            )}

            {/* Participants chips — pickers/pressers/packers who've touched
                this order. Renders directly under the Working-at banner. */}
            <PortalParticipantsFooter
                orderId={orderId}
                storeKey={orderStoreKey}
                fetchFn={fetchFn}
                roles={['pickers', 'pressers']}
            />


            {/* Sticky top group — header + scan + sort all stick together */}
            <div className="pkp-sticky-top">
            {/* Header */}
            <div className="pkp-header">
                <button onClick={handleBackAndSave} className="pkp-back-btn">{t('← Back & Save')}</button>
                <div className="pkp-header-center">
                    <div className="pkp-order-name">{order?.order_name}</div>
                    {order?.club && <div className="pkp-club">{order.club}</div>}
                    {order?.order_created_at && (
                        <div className="pkp-order-date">
                            {new Date(order.order_created_at).toLocaleDateString(undefined,
                                { month: 'short', day: 'numeric', year: 'numeric' })}
                        </div>
                    )}
                </div>
                <div className="pkp-progress-badge">{pickedCount}/{displayGroups.length} ✓</div>
            </div>

            {/* Scan bar */}
            <div className="pkp-scan-bar">
                <input
                    ref={scanRef}
                    type="text"
                    value={scanInput}
                    onChange={e => setScanInput(e.target.value)}
                    onKeyDown={handleScanKeyDown}
                    placeholder={t('Scan barcode…')}
                    className="pkp-scan-input"
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                />
                <button
                    className="pkp-camera-btn"
                    onClick={() => { setCameraOpen(true); }}
                    title={t('Open camera scanner')}
                >📷</button>
            </div>

            {/* Camera overlay */}
            {cameraOpen && (
                <CameraScanner
                    onDetect={(barcode) => {
                        setCameraOpen(false);
                        processBarcode(barcode);
                        refocusScan();
                    }}
                    onClose={() => { setCameraOpen(false); refocusScan(); }}
                />
            )}

            {/* Complete error */}
            {completeError && (
                <div className="pkp-complete-error">{completeError}</div>
            )}

            {/* Debag confirmation banner */}
            {debagBanner && (
                <div className="pkp-error-msg" style={{ padding: '4px 4px 8px' }}>{debagBanner}</div>
            )}

            {/* Sort + type-filter controls (two segmented groups) */}
            <div className="pkp-sort-bar">
                <span className="pkp-sort-label">{t('Sort:')}</span>
                <div className="pkp-seg">
                    {[['location', t('Location')], ['type', t('Type')]].map(([val, label]) => (
                        <button
                            key={val}
                            onClick={() => setSortBy(val)}
                            className={`pkp-sort-btn${sortBy === val ? ' pkp-sort-btn--active' : ''}`}
                        >{label}</button>
                    ))}
                </div>
                <span className="pkp-sort-label pkp-sort-label--gap">{t('Show:')}</span>
                <div className="pkp-seg">
                    {[['all', t('All')], ['base', t('Base')], ['embellishments', t('Embel.')]].map(([val, label]) => (
                        <button
                            key={val}
                            onClick={() => setTypeFilter(val)}
                            className={`pkp-sort-btn${typeFilter === val ? ' pkp-sort-btn--active' : ''}`}
                        >{label}</button>
                    ))}
                </div>
            </div>
            </div>{/* end pkp-sticky-top */}

            {/* Item list */}
            <div className="pkp-item-list">
                {sortedDisplayGroups.map(g => {
                    // For aggregated SKU-less groups, sum picked counts across all
                    // underlying sku_groups indices. For normal groups the array has
                    // exactly one index so this degenerates to the original logic.
                    const displayPicked = (g._underlyingIndices || []).reduce((s, idx) => s + (picked[idx] || 0), 0);
                    const done = displayPicked >= g.quantity;
                    const firstIdx = (g._underlyingIndices || [])[0] ?? 0;
                    const rawGroups = order?.sku_groups || [];
                    // Debag confirmation gate: bundle base rows carry g.debag from the
                    // backend flag pass. isPendingDebag drives the amber bar; isDebaggedRow
                    // drives the grey "Debagged ✓" chip once resolved.
                    const debagIdxForRow = (g._underlyingIndices || []).find(i => rawGroups[i]?.debag);
                    const isPendingDebag = debagIdxForRow !== undefined && pendingDebagIdx === debagIdxForRow;
                    const isDebaggedRow = debagIdxForRow !== undefined && !!debagged[debagIdxForRow];
                    return (
                        <div
                            key={firstIdx}
                            className={`pkp-item-card${(done && !isPendingDebag) ? ' pkp-item-card--done' : ''}`}
                        >
                            {isPendingDebag && (
                                <button
                                    type="button"
                                    className="pkp-debag-bar"
                                    onClick={() => debag(debagIdxForRow)}
                                >
                                    🧺 {t('Debag — {title}', { title: g.title })}
                                </button>
                            )}
                            <div className="pkp-item-row">
                            {(() => {
                                const preview = g.product_id ? numberPreviews[g.product_id] : null;
                                const num = (g.players || []).map(p => p.number).find(Boolean)
                                    || preview?.defaultValue || '';
                                if (preview && num) {
                                    return (
                                        <div className="pkp-item-img" style={{ padding: 0, overflow: 'hidden' }}>
                                            <NumberPreviewThumb preview={preview} number={num} size={48} borderColor="transparent" />
                                        </div>
                                    );
                                }
                                if (g.image_url) {
                                    return <SmartProductImage src={g.image_url} alt={g.title} className="pkp-item-img" />;
                                }
                                return <div className="pkp-item-img-placeholder">📦</div>;
                            })()}
                            <div className="pkp-item-info">
                                <div className="pkp-item-title">{g.title}</div>
                                {g.variant_title && (
                                    <span className="pkp-item-variant-badge">{g.variant_title}</span>
                                )}
                                {g.sku && (
                                    <div className="pkp-item-sku">
                                        {g.sku}
                                        {isDebaggedRow && <span className="pkp-debag-chip">{t('Debagged')} ✓</span>}
                                    </div>
                                )}
                                {g.sku && skuNotifications[g.sku] && (
                                    <div style={{
                                        display: 'inline-block',
                                        marginTop: 4,
                                        padding: '3px 8px',
                                        borderRadius: 12,
                                        background: '#fde8e8',
                                        color: '#c00',
                                        fontWeight: 700,
                                        fontSize: '0.85em',
                                        border: '1px solid #fbb',
                                    }}>
                                        {skuNotifications[g.sku]}
                                    </div>
                                )}
                                {g.location_value && (
                                    <div className="pkp-item-location">📍 {g.location_value}</div>
                                )}
                                {g.overstock_value && (
                                    <div className="pkp-item-location pkp-item-location--overstock"
                                         title={t('Overstock')}>
                                        📦 {g.overstock_value}
                                    </div>
                                )}
                                {(g.players || []).map((p, pi) => {
                                    // TP override: RDS number wins if the club
                                    // is on the Team Portal override list.
                                    // Yellow-highlight the swapped digit.
                                    // Name lookup is primary; number lookup
                                    // is the fallback for anonymous Number
                                    // embellishment groups (Stahls tiles),
                                    // which surface as {name: null, number}.
                                    const ov = (p.name && tpOverridesByName[p.name])
                                        || (p.number != null && tpOverridesByOrderNumber[String(p.number)])
                                        || null;
                                    const isOverride = !!(ov && String(ov.override_number || '') !== String(p.number || ''));
                                    const displayNumber = isOverride ? ov.override_number : p.number;
                                    return (
                                        <div key={pi} className="pkp-item-player">
                                            {p.name}
                                            {displayNumber && (
                                                <>
                                                    {' '}
                                                    <span style={isOverride ? {
                                                        background: '#fef3c7',
                                                        color: '#78350f',
                                                        padding: '1px 6px',
                                                        borderRadius: 4,
                                                        border: '1px solid #fbbf24',
                                                        fontWeight: 600,
                                                    } : undefined}
                                                          title={isOverride
                                                              ? `Team Portal override — order had #${p.number}, updated to #${displayNumber}`
                                                              : undefined}>
                                                        #{displayNumber}
                                                    </span>
                                                </>
                                            )}
                                            {p.label && <>{' — '}{p.label}</>}
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="pkp-item-right">
                                {(g.on_hand != null || g.available != null) && (
                                    <div className="pkp-inv-badges">
                                        {g.on_hand != null && <div className="pkp-item-on-hand">{t('On Hand:')} {g.on_hand}</div>}
                                        {g.available != null && <div className="pkp-item-avail">{t('Available:')} {g.available}</div>}
                                    </div>
                                )}
                                <div className={`pkp-item-count${done ? ' pkp-item-count--full' : displayPicked > 0 ? ' pkp-item-count--partial' : ''}`}>
                                    <span className="pkp-item-count--current">{displayPicked}</span>
                                    <span className="pkp-item-count--sep">/</span>
                                    <span className="pkp-item-count--total">{g.quantity}</span>
                                </div>
                                {/* Always render the +/- buttons. The `-` is enabled whenever
                                    displayPicked > 0 (lets scan-only pickers correct a wrong scan AND
                                    unpick stale prior-session picks). The `+` is disabled for
                                    scan_only_picker users at Warehouse locations (strict) or on
                                    products that opt INTO scanning via custom.require_scanning="true".
                                    require_scanning="false" or absent at a non-Warehouse location
                                    leaves `+` enabled — the per-product opt-out. */}
                                <div className="pkp-qty-btns">
                                    <button
                                        className="pkp-qty-btn pkp-qty-btn--minus"
                                        onClick={() => {
                                            // FIFO-last: find the highest-index underlying row with picks > 0
                                            const indices = g._underlyingIndices || [];
                                            const targetIdx = [...indices].reverse().find(i => (picked[i] || 0) > 0);
                                            if (targetIdx === undefined) return;
                                            let _newVal = 0;
                                            setPicked(p => {
                                                const newVal = Math.max(0, (p[targetIdx] || 0) - 1);
                                                _newVal = newVal;
                                                setScannedCounts(s => ({ ...s, [targetIdx]: Math.min(s[targetIdx] || 0, newVal) }));
                                                return { ...p, [targetIdx]: newVal };
                                            });
                                            // Debag confirmation gate: dropping below full quantity
                                            // clears the debagged flag — picking back to full re-triggers the bar.
                                            clearDebagIfNeeded(targetIdx, _newVal);
                                        }}
                                        disabled={displayPicked <= 0}
                                    >−</button>
                                    <button
                                        className="pkp-qty-btn pkp-qty-btn--plus"
                                        onClick={() => {
                                            // FIFO-first: find the lowest-index underlying row with room
                                            const indices = g._underlyingIndices || [];
                                            const targetIdx = indices.find(
                                                i => (picked[i] || 0) < (rawGroups[i]?.quantity || 0)
                                            );
                                            if (targetIdx === undefined) return;
                                            // Debag hard gate: a bar is pending on a DIFFERENT row —
                                            // buzz + banner, tap ignored (count does not change).
                                            if (pendingDebagIdx >= 0 && pendingDebagIdx !== targetIdx) {
                                                playError();
                                                setDebagBanner(t('Debag {title} before continuing', { title: rawGroups[pendingDebagIdx].title }));
                                                return;
                                            }
                                            setPicked(p => ({ ...p, [targetIdx]: (p[targetIdx] || 0) + 1 }));
                                            setMarkedAt(p => ({ ...p, [targetIdx]: new Date().toISOString() }));
                                            triggerFlash('green');
                                            playSuccess();
                                        }}
                                        disabled={(() => {
                                            if (done) return true;
                                            const scanOnly = !!presserSession?.scan_only_picker && (isAtWarehouse || g.require_scanning === true);
                                            if (!scanOnly) return false;
                                            // Manager override unlocks EVERY SKU on this order
                                            // for the duration of the 60s window.
                                            if (managerActive) return false;
                                            return true;
                                        })()}
                                    >+</button>
                                </div>
                            </div>
                            </div>{/* end pkp-item-row */}
                        </div>
                    );
                })}
            </div>

            {/* Completion modal */}
            {showCompleteModal && ReactDOM.createPortal(
                <div className="pkp-complete-modal-backdrop">
                    <div className="pkp-complete-modal">
                        <div className="pkp-complete-modal-icon">✅</div>
                        <div className="pkp-complete-modal-title">{t('Complete!')}</div>
                        <div className="pkp-complete-modal-sub">{order?.order_name}</div>
                        {completeError && (
                            <div className="pkp-complete-modal-error">{completeError}</div>
                        )}
                        {pendingDebagIdx >= 0 && (
                            <div className="pkp-complete-modal-error">
                                {t('Debag {title} before continuing', { title: (order?.sku_groups || [])[pendingDebagIdx]?.title || '' })}
                            </div>
                        )}
                        <button
                            className="pkp-complete-modal-btn pkp-complete-modal-btn--primary"
                            onClick={handleComplete}
                            disabled={completing || pendingDebagIdx >= 0}
                        >
                            {completing ? t('Saving…') : t('✓ Mark as Picked & Back to Search')}
                        </button>
                        <button
                            className="pkp-complete-modal-btn pkp-complete-modal-btn--secondary"
                            onClick={() => setShowCompleteModal(false)}
                            disabled={completing}
                        >
                            {t('← Return to Order')}
                        </button>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}

// ── Main PickingPortal component ────────────────────────────────────────────

export default function PickingPortal({ fullscreen = false }) {
    const t = useT();
    const { user, emulating, startEmulation }     = useAuth();
    const { activeStore }         = useStore();
    const { orderId: urlOrderId } = useParams();
    const navigate                = useNavigate();
    const location                = useLocation();
    const returnTo                = location.state?.returnTo || null;
    const [presserSession]        = useState(() => getPresserSession());
    const [selectedOrderId, setSelectedOrderId] = useState(null);
    const [selectedOrderContext, setSelectedOrderContext] = useState(null); // { orderShopifyLocationId, orderStoreKey }
    const [bootstrapError, setBootstrapError]   = useState('');
    const [isFullscreen, setIsFullscreen]       = useState(!!document.fullscreenElement);
    // Admin-mode loc picker: shown when the user opens an order in ?loc=all
    // (or any state where legacyShopifyLocationId is empty) and the order
    // has multiple candidate locations. Blocks the picker view until the
    // user commits to a physical loc so pick events land with a real
    // shopify_location_id instead of an empty string.
    const [adminLocPickerOrderId, setAdminLocPickerOrderId]     = useState(null);
    const [adminLocPickerCandidates, setAdminLocPickerCandidates] = useState([]);

    const isStandalone = window.navigator.standalone === true ||
                         window.matchMedia('(display-mode: standalone)').matches;

    // Multi-location assignments (PIN-only session). Legacy/admin path falls back below.
    const assignments = useMemo(() => presserSession?.assignments || [], [presserSession]);
    const locationMap = useMemo(() => {
        const m = new Map();
        for (const a of assignments) {
            m.set(String(a.shopify_location_id), { store_key: a.store_key, location_name: a.location_name });
        }
        return m;
    }, [assignments]);

    // Session guard: fullscreen mode requires a picker session
    useEffect(() => {
        if (fullscreen) {
            if (!presserSession || !(presserSession.portal_type === 'picker' || (presserSession.roles || []).includes('picker'))) {
                window.location.href = '/pressing-portal/login';
            }
        }
    }, [fullscreen, presserSession]);

    // Trap browser back button in fullscreen portal to avoid redirect loops.
    useEffect(() => {
        if (!fullscreen) return;
        window.history.pushState(null, '', window.location.href);
        const trap = () => window.history.pushState(null, '', window.location.href);
        window.addEventListener('popstate', trap);
        return () => window.removeEventListener('popstate', trap);
    }, [fullscreen]);

    // Idle lock: 15 min of inactivity in fullscreen portal → PIN screen.
    // Fire session-close for currently-open order first so its end is bracketed.
    useIdleLock(fullscreen && !!presserSession, IDLE_LOCK_MS, () => {
        if (selectedOrderId && selectedOrderContext) {
            try {
                pressingFetch(`/api/picking/orders/${selectedOrderId}/session-close?store=${selectedOrderContext.orderStoreKey || 'soccerpost'}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        portal_userid:       presserSession?.presser_id || null,
                        portal_username:     presserSession
                            ? `${presserSession.first_name} ${presserSession.last_name}` : '',
                        shopify_location_id: selectedOrderContext?.orderShopifyLocationId || '',
                    }),
                    keepalive: true,
                }).catch(() => {});
            } catch {}
        }
        clearPressingSession();
        // Re-stash the kiosk token so PIN login still has a valid admin token
        const kiosk = localStorage.getItem('ops_portal_token');
        if (kiosk) sessionStorage.setItem('pressing_admin_token', kiosk);
        window.location.href = '/pressing-portal/login';
    });

    // pagehide / visibilitychange='hidden' flush a session-close on tab close,
    // iPad swipe-away, app force-quit. `keepalive: true` lets the browser ship
    // the POST during teardown.
    //
    // Use authenticatedFetch in admin sidebar mode (no presser session) —
    // pressingFetch's "no token" branch does `window.location.href = '/login'`
    // which fires on every tab-blur and bounces this tab to the orders list
    // when the user clicks another tab.
    useEffect(() => {
        const flushClose = () => {
            if (!selectedOrderId || !selectedOrderContext) return;
            const fetcher = presserSession ? pressingFetch : authenticatedFetch;
            try {
                fetcher(`/api/picking/orders/${selectedOrderId}/session-close?store=${selectedOrderContext.orderStoreKey || 'soccerpost'}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        portal_userid:       presserSession?.presser_id || null,
                        portal_username:     presserSession
                            ? `${presserSession.first_name} ${presserSession.last_name}` : '',
                        shopify_location_id: selectedOrderContext?.orderShopifyLocationId || '',
                    }),
                    keepalive: true,
                }).catch(() => {});
            } catch {}
        };
        const onVisibility = () => {
            if (document.visibilityState === 'hidden') flushClose();
        };
        window.addEventListener('pagehide', flushClose);
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            window.removeEventListener('pagehide', flushClose);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [selectedOrderId, selectedOrderContext, presserSession]);

    // Auto-fullscreen on launch — iPad/Android kiosk only. Desktop runs as a
    // normal tab (matches QCPortal) so print/barcode-scan dialogs don't get
    // blocked by fullscreen.
    useEffect(() => {
        const isDesktop = isDesktopBrowser();   // 2026-10-14: iPadOS 27 no longer reports maxTouchPoints > 1 (constants/deviceDetect.js)
        if (isDesktop) return;
        if (!fullscreen || isStandalone) return;
        if (document.fullscreenElement) return;
        if (document.documentElement.requestFullscreen) {
            document.documentElement.requestFullscreen().catch(() => {});
        }
    }, [fullscreen]); // eslint-disable-line react-hooks/exhaustive-deps

    // For legacy admin-sidebar path only. Multi-assignment sessions derive loc/store per order.
    // 2026-07-07: fall back to a loc that MATCHES the current store, not the
    // JWT-primary (which is a single global value and can be for a different
    // store entirely). Under the old logic, a user with 1 surf loc browsing
    // /store/surf/picking-portal fell back to their soccerpost primary and
    // the API scoped the pick list to the wrong loc ("No items to pick").
    const _currentStoreLoc = (user?.locations || []).find(l => l.store_key === activeStore);
    const legacyShopifyLocationId =
        emulating?.shopify_location_id
        || _currentStoreLoc?.shopify_location_id
        || user?.shopify_location_id
        || '';

    // Admin-mode helper: resolve which physical loc to record picks against.
    // Order of preference:
    //   1. `?loc=<sid>` URL param (validated against order's cache-locations).
    //   2. `legacyShopifyLocationId` if it's non-empty AND matches a loc the
    //      order actually has (avoids the pre-fix bug where the JWT primary
    //      overrode the order's real loc).
    //   3. Single-loc order → auto-select.
    //   4. Multi-loc order → open LocationPickerModal, block until user picks.
    // Returns { shopify_location_id, store_key } when resolved synchronously,
    // or null when it opened the picker (caller must not proceed until the
    // picker commits).
    const resolveAdminOrderLoc = useCallback(async (orderId) => {
        const storeKey = activeStore || 'soccerpost';
        // Fetch what locs this order actually has (from order_cache rows).
        let locs = [];
        try {
            const r = await authenticatedFetch(`/api/orders/${orderId}/cache-locations?store=${storeKey}`);
            if (r.ok) {
                const j = await r.json();
                locs = (j.locations || []).filter(l => l && l.shopify_location_id);
            }
        } catch { /* fall through */ }
        // Once we've picked ONE loc, mirror it to the sidebar dropdown via
        // startEmulation so the top-right chip reads the actual location
        // instead of "All Locations" (matches the user's mental model —
        // they're now working AT that specific loc).
        const _commit = (loc) => {
            try {
                startEmulation?.({
                    id:                  loc.id ?? null,
                    name:                loc.name || loc.location_name || '',
                    shopify_location_id: loc.shopify_location_id,
                    is_franchise:        !!loc.is_franchise,
                    store_key:           storeKey,
                });
            } catch { /* non-fatal */ }
            return { shopify_location_id: loc.shopify_location_id, store_key: storeKey };
        };
        // Honor ?loc=<sid> when it names a loc the order actually has.
        const urlLoc = new URLSearchParams(location.search).get('loc') || '';
        if (urlLoc && urlLoc !== 'all') {
            const hit = locs.find(l => String(l.shopify_location_id) === String(urlLoc));
            if (hit) return _commit(hit);
        }
        // Legacy fallback only if it names a loc the order actually has.
        if (legacyShopifyLocationId) {
            const hit = locs.find(l => String(l.shopify_location_id) === String(legacyShopifyLocationId));
            if (hit) return _commit(hit);
        }
        if (locs.length === 1) return _commit(locs[0]);
        if (locs.length > 1) {
            // Reshape into what LocationPickerModal expects (location_name key).
            setAdminLocPickerCandidates(locs.map(l => ({
                id:                  l.id ?? null,
                shopify_location_id: l.shopify_location_id,
                location_name:       l.name || l.location_name || l.shopify_location_id,
                is_franchise:        !!l.is_franchise,
                store_key:           storeKey,
            })));
            setAdminLocPickerOrderId(orderId);
            return null;
        }
        // No cache-locations at all — happens for freshly-webhook'd orders
        // whose cache hasn't hydrated yet. Fall back to legacy so the flow
        // still works; downstream cache-side effects may still be off but
        // that's the pre-fix behavior.
        return {
            shopify_location_id: legacyShopifyLocationId || '',
            store_key:           storeKey,
        };
    }, [activeStore, legacyShopifyLocationId, location.search, startEmulation]);

    // Bootstrap: open order from direct URL (bookmarked /picking-portal/active/orders/:id).
    // Multi-assignment sessions can either:
    //   (a) include a `?loc=<shopify_location_id>` query param to pin the loc, or
    //   (b) get pushed to the Search page so we can resolve the loc per order.
    useEffect(() => {
        // Admin sidebar: no session needed, just open the order — but resolve
        // the physical loc first (?loc=all + JWT-fallback used to produce an
        // empty shopify_location_id → pick events landed loc-less and never
        // updated the cache row's items_picked_total).
        if (urlOrderId && !presserSession) {
            (async () => {
                const resolved = await resolveAdminOrderLoc(urlOrderId);
                if (resolved) {
                    setSelectedOrderId(urlOrderId);
                    setSelectedOrderContext({
                        orderShopifyLocationId: resolved.shopify_location_id,
                        orderStoreKey:          resolved.store_key,
                    });
                }
                // If resolved is null, the loc picker modal is now open;
                // its onPick handler will set the context and selectedOrderId.
            })();
            return;
        }
        if (!urlOrderId || !presserSession || !fullscreen) return;
        // Honor `?loc=<sid>` when supplied — must be one of this user's
        // assigned locations, otherwise ignore.
        const _locFromUrl = new URLSearchParams(location.search).get('loc') || '';
        if (_locFromUrl) {
            const hit = assignments.find(a => String(a.shopify_location_id) === String(_locFromUrl));
            if (hit) {
                setSelectedOrderId(urlOrderId);
                setSelectedOrderContext({
                    orderShopifyLocationId: hit.shopify_location_id,
                    orderStoreKey: hit.store_key,
                });
                return;
            }
        }
        // Multi-assignment without a ?loc= hint: cannot pre-resolve → ask user to use search.
        if (assignments.length !== 1) {
            setBootstrapError(t('Please open this order from the search page.'));
            return;
        }
        // Single-assignment: open immediately at that location/store
        const only = assignments[0];
        setSelectedOrderId(urlOrderId);
        setSelectedOrderContext({
            orderShopifyLocationId: only.shopify_location_id,
            orderStoreKey: only.store_key,
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps -- run once on mount

    useEffect(() => {
        const onChange = () => setIsFullscreen(!!document.fullscreenElement);
        document.addEventListener('fullscreenchange', onChange);
        return () => document.removeEventListener('fullscreenchange', onChange);
    }, []);

    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen().catch(() => {});
        } else {
            document.exitFullscreen().catch(() => {});
        }
    };

    const handleSelect = async (id, ctx = null) => {
        if (ctx) {
            setSelectedOrderId(id);
            setSelectedOrderContext(ctx);
        } else if (!presserSession) {
            // Admin sidebar: resolve the order's real loc via
            // resolveAdminOrderLoc — either auto-set (single loc) or open the
            // LocationPickerModal (multi loc). Never fall back to an empty
            // shopify_location_id.
            const resolved = await resolveAdminOrderLoc(id);
            if (resolved) {
                setSelectedOrderId(id);
                setSelectedOrderContext({
                    orderShopifyLocationId: resolved.shopify_location_id,
                    orderStoreKey:          resolved.store_key,
                });
            }
            // else: picker modal now open; onPick will commit.
        } else {
            setSelectedOrderId(id);
        }
        if (fullscreen) {
            // Include ?loc=<sid> so a reload/bookmark of the resulting URL
            // survives the multi-assignment bootstrap guard.
            const _sid = ctx?.orderShopifyLocationId || legacyShopifyLocationId || '';
            const _qs = _sid ? `?loc=${encodeURIComponent(_sid)}` : '';
            navigate(`/picking-portal/active/orders/${id}${_qs}`);
        }
    };

    const handleBack = () => {
        if (returnTo) { navigate(returnTo); return; }
        setSelectedOrderId(null);
        setSelectedOrderContext(null);
        if (fullscreen) navigate('/picking-portal/active');
    };

    // Header location text:
    //  - presser with single assignment: show that location name
    //  - presser with multiple assignments: show "Multiple locations (N)"
    //  - legacy/admin: existing fallback
    let locationName = '';
    if (presserSession) {
        if (assignments.length === 1) locationName = assignments[0].location_name || '';
        else if (assignments.length > 1) locationName = t('Multiple locations ({n})', { n: assignments.length });
        else locationName = presserSession.location_name || '';
    } else {
        locationName = emulating?.name || user?.location_name || '';
    }
    const pickerName = presserSession
        ? `${presserSession.first_name} ${presserSession.last_name}`
        : user?.user;

    // fetchFn signature: (url, opts, storeKeyOverride = null).
    // Default store resolution (for admin sidebar + single-assignment sessions):
    //   presser single-assignment → that assignment's store_key
    //   admin sidebar              → activeStore
    const defaultStoreKey = presserSession
        ? (assignments[0]?.store_key || activeStore || 'soccerpost')
        : (activeStore || 'soccerpost');
    const baseFetchFn = presserSession ? pressingFetch : authenticatedFetch;
    const fetchFn = useCallback((url, opts, storeKeyOverride = null) => {
        const sk = storeKeyOverride || defaultStoreKey;
        const fullUrl = url.includes('?store=') || url.includes('&store=')
            ? url
            : `${url}${url.includes('?') ? '&' : '?'}store=${sk}`;
        return baseFetchFn(fullUrl, opts);
    }, [baseFetchFn, defaultStoreKey]);

    const handleSignOut = () => {
        sessionStorage.removeItem('presser_session');
        window.location.href = '/pressing-portal/login';
    };

    // Derive the location name for the "Working at" banner.
    // Shown only when the user has multiple assignments and explicitly chose one.
    const orderLocationName = useMemo(() => {
        if (!selectedOrderContext) return null;
        if (!presserSession || assignments.length <= 1) return null;
        const locId = selectedOrderContext.orderShopifyLocationId;
        if (!locId) return null;
        const a = assignments.find(a => String(a.shopify_location_id) === String(locId));
        return a?.location_name || null;
    }, [selectedOrderContext, presserSession, assignments]);

    // fetchFn wrapper that adds X-Emulate-Location when a specific order-level
    // location has been chosen. This ensures picking state is recorded at the
    // correct location on the backend.
    const orderFetchFn = useCallback((url, opts, storeKeyOverride = null) => {
        if (!selectedOrderContext?.orderShopifyLocationId) return fetchFn(url, opts, storeKeyOverride);
        const mergedOpts = {
            ...opts,
            headers: {
                ...(opts?.headers || {}),
                'X-Emulate-Location': String(selectedOrderContext.orderShopifyLocationId),
            },
        };
        return fetchFn(url, mergedOpts, storeKeyOverride);
    }, [fetchFn, selectedOrderContext]);

    const content = (
        <>
            {!selectedOrderId && (
                <>
                    <OrderSearch
                        shopifyLocationId={legacyShopifyLocationId}
                        fetchFn={fetchFn}
                        presserSession={presserSession}
                        assignments={assignments}
                        locationMap={locationMap}
                        onSelect={handleSelect}
                        bootstrapError={bootstrapError}
                    />
                    <PortalDailySummary
                        portal="picking"
                        presserSession={presserSession}
                        fetchFn={fetchFn}
                    />
                </>
            )}
            {selectedOrderId && selectedOrderContext && (
                <PickingView
                    orderId={selectedOrderId}
                    orderShopifyLocationId={selectedOrderContext.orderShopifyLocationId}
                    orderStoreKey={selectedOrderContext.orderStoreKey}
                    orderLocationName={orderLocationName}
                    presserSession={presserSession}
                    fetchFn={orderFetchFn}
                    onBack={handleBack}
                />
            )}
            {/* Admin-mode loc picker: shown when a direct-link admin opens an
                order without a resolvable loc (?loc=all + multi-loc order). */}
            {adminLocPickerCandidates.length > 0 && adminLocPickerOrderId && (
                <LocationPickerModal
                    candidates={adminLocPickerCandidates}
                    onPick={(loc) => {
                        setSelectedOrderId(adminLocPickerOrderId);
                        setSelectedOrderContext({
                            orderShopifyLocationId: loc.shopify_location_id,
                            orderStoreKey:          loc.store_key || activeStore || 'soccerpost',
                        });
                        setAdminLocPickerCandidates([]);
                        setAdminLocPickerOrderId(null);
                    }}
                    onCancel={() => {
                        setAdminLocPickerCandidates([]);
                        setAdminLocPickerOrderId(null);
                        // Send admin back to Orders — cancelling out of the
                        // loc picker without a selection means we can't
                        // safely open the picking view.
                        navigate(returnTo || `/store/${activeStore || 'soccerpost'}/orders`);
                    }}
                />
            )}
        </>
    );

    if (fullscreen) {
        return (
            <div className="pp-fullscreen">
                {/* iOS Add to Home Screen prompt — shown only when not already standalone */}
                {!isStandalone && (
                    <div className="pkp-a2hs-banner">
                        <span className="pkp-a2hs-icon">📲</span>
                        <span className="pkp-a2hs-text">
                            {t('For fullscreen: tap')} <strong>{t('Share')}</strong> → <strong>{t('Add to Home Screen')}</strong>
                        </span>
                    </div>
                )}
                {/* Header banner only on order search, not on the picking view */}
                {!selectedOrderId && (
                    <div className="pp-header">
                        <div className="pp-header__left">
                            <span className="pp-header__icon">📦</span>
                            <div>
                                <div className="pp-header__title">{t('Picking Portal')}</div>
                                <div className="pp-header__sub">
                                    {locationName || t('No location')}
                                </div>
                                <div className="pp-header__sub">
                                    {pickerName || ''}
                                </div>
                            </div>
                        </div>
                        <div className="pp-header__right">
                            {presserSession && (
                                <button onClick={handleSignOut} className="pp-header-btn pp-header-btn--sign-out">
                                    {t('↩ Sign Out')}
                                </button>
                            )}
                        </div>
                    </div>
                )}
                <div className="pp-content">{content}</div>
            </div>
        );
    }

    return (
        <div className="pp-page">
            <div className="pp-page-header">
                <div>
                    <h1 className="pp-page-title">{t('Picking Portal')}</h1>
                    {locationName && <div className="pp-page-loc">{locationName}</div>}
                </div>
            </div>
            {content}
        </div>
    );
}
